/**
 * L'IMPORT, lot 3 : l'écran.
 *
 * Un onglet du module Paramètres — c'est là que l'utilisateur avait tranché le
 * 03/09/2026 que la reprise des données vivrait : « ce n'est pas une étape de
 * développement, mais un usage de la future fonction d'import ».
 *
 *
 * TROIS GESTES, DANS CET ORDRE, ET L'ÉCRAN LES NUMÉROTE
 * ---------------------------------------------------------------------------
 *   1. TÉLÉCHARGER LE MODÈLE. Le format est imposé par Stafa — décidé le
 *      07/09 — donc l'écran doit le donner, sinon personne ne devine les noms
 *      de colonnes ;
 *   2. DÉPOSER LE FICHIER REMPLI. Le dépôt déclenche l'ANALYSE, qui n'écrit
 *      rien. C'est volontaire : on ne demande pas à l'utilisateur de cliquer
 *      « analyser » alors qu'il n'y a rien d'autre à faire d'un fichier qu'on
 *      vient de recevoir ;
 *   3. IMPORTER. Le bouton ne s'allume que si le fichier est parfait, parce que
 *      l'import est TOUT OU RIEN. Un bouton actif devant un fichier fautif
 *      promettrait ce qu'il ne tiendrait pas.
 *
 *
 * LE MODÈLE SE TÉLÉCHARGE EN OCTETS, JAMAIS EN TEXTE
 * ---------------------------------------------------------------------------
 * `Response.text()` RETIRE la marque d'ordre d'octets en décodant — c'est le
 * standard fetch, constaté le 07/09/2026 en écrivant le test de la route.
 * Écrit par `.text()`, le modèle arriverait SANS marque, Excel l'ouvrirait en
 * Windows-1252, et tous les accents reviendraient cassés à l'import.
 * `telechargerModele` passe donc par `blob()`, et ce commentaire est là pour
 * que personne ne « simplifie » cela un jour.
 */

import { useCallback, useRef, useState } from 'react';
import {
  analyserImport,
  lancerImport,
  telechargerModele,
  type EchecApi,
  type RapportImport,
  type SorteImport,
} from './api.js';
import {
  apercuDit,
  importDit,
  nomModele,
  peutImporter,
  resumeAnalyse,
  sorteDite,
  SORTES_IMPORT,
} from './import.js';

/** Ce que l'API a refusé, dit en français. */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette sorte de fichier n’existe pas. Rechargez l’écran.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Un code de ce fichier est déjà pris.',
  invalide: 'Le fichier n’a pas été accepté.',
  conflit: 'Deux périodes se recouvrent.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Des fiches s’y rattachent encore.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee:
    'Une étape déjà validée ne se déplace plus, et ne perd plus son camion.',
  jour_sans_etape: 'Aucune étape ce jour-là.',
  etape_en_tournee: 'Une étape de cet ordre appartient à une tournée.',
  // LES DEUX REFUS DU BOUCLAGE D'UNE JOURNÉE, ajoutés le 14/09/2026. Cette
  // carte doit rester EXHAUSTIVE : c'est elle qui garantit qu'aucun refus de
  // l'API ne s'affichera un jour sous forme de code brut.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté : renseignez son lieu de fin d’abord.',
  litige_deja_ouvert: 'Cette étape a déjà un litige ouvert.',
  litige_deja_resolu: 'Ce litige est déjà résolu.',
  // LES SIX REFUS DE LA FACTURATION, ajoutés le 09/09/2026. Ils n'arrivent pas
  // sur cet écran — la carte reste exhaustive, c'est ce qui garantit qu'aucun
  // refus de l'API ne s'affichera un jour sous forme de code brut.
  litige_ouvert: 'Cet ordre porte un litige ouvert : il ne se facture pas.',
  deja_facture: 'Cet ordre est déjà porté par une facture.',
  rien_a_facturer: 'Aucun de ces ordres n’attend de facture.',
  facture_emise: 'Cette pièce est émise : elle se corrige par un avoir.',
  facture_brouillon: 'Un brouillon ne s’envoie pas : validez-le d’abord.',
  facture_vide: 'Une facture sans ligne ne se valide pas.',
  // AJOUTÉ LE 09/09/2026. Un ordre parti en facturation ne se modifie plus :
  // il faut le retirer de la facturation d'abord.
  ordre_en_facturation:
    'Cet ordre est parti en facturation : retirez-le de la facturation avant de le modifier.',
  // LES CINQ REFUS DU RECOUVREMENT, ajoutés le 10/09/2026. Chacun dit la SUITE,
  // pas seulement le refus : c'est ce qui distingue un état d'une saisie fautive.
  affectation_excessive:
    'Vous affectez plus que ce qui a été reçu : corrigez la ventilation.',
  piece_non_emise:
    'Cette pièce est un brouillon : validez-la avant de la régler ou de la relancer.',
  piece_autre_client: 'Cette pièce appartient à un autre client.',
  gel_deja_actif:
    'Ce client a déjà un gel en cours : levez-le avant d’en poser un autre.',
  gel_absent: 'Ce client n’a aucun gel en cours.',
  // LES DEUX REFUS DE L'ANNULATION DE VALIDATION, ajoutés le 10/09/2026.
  numero_pas_le_dernier:
    'Cette pièce ne porte pas le dernier numéro de sa série : lui rendre son numéro laisserait un trou. Corrigez-la par un avoir.',
  facture_reglee:
    'Un règlement est affecté à cette pièce : détachez-le avant d’annuler la validation.',
  // AJOUTÉ LE 10/09/2026, sur un 500 constaté EN PRODUCTION : la base
  // refusait déjà (relance_pieces pointe sur factures en `on delete
  // restrict`), mais son refus sortait en erreur interne et l'écran
  // parlait d'une panne.
  piece_relancee:
    'Une relance a été envoyée sur cette pièce : elle ne peut plus être supprimée. Corrigez-la par un avoir.',
  facture_sans_echeance:
    'Cette pièce n’a pas de date d’échéance : sans elle, le recouvrement ne saura jamais qu’elle est en retard.',
  // AJOUTÉ LE 11/09/2026 avec le rattachement Pennylane. Distinct de
  // `code_pris` : le geste de réparation n'est pas le même. Le message NOMME
  // ce qu'il faut regarder — sans quoi il faudrait ouvrir les neuf autres
  // fiches pour trouver celle qui tient déjà ce client.
  pennylane_deja_rattache:
    'Ce client Pennylane est déjà rattaché à une autre fiche de l’annuaire. Le plus souvent, c’est que deux fiches Stafa désignent le même client : vérifiez vos doublons.',
  // AJOUTÉS LE 11/09/2026 avec l'envoi d'une facture chez Pennylane. Deux
  // messages et pas un : le premier mène à un AUTRE ÉCRAN, le second demande
  // de corriger la pièce. L'écran ne propose pas la même suite.
  pennylane_client_non_rattache:
    'Le client de cette facture n’est pas rattaché à un client Pennylane. Allez dans Paramètres → Pennylane pour le rattacher, puis réessayez.',
  pennylane_impossible:
    'Cette facture n’a pas pu être envoyée chez Pennylane. Le détail est affiché sous ce message : il vient de Pennylane et dit ce qui ne va pas.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  // LES QUATRE REFUS DE L'AVOIR, ajoutés le 11/09/2026. Quatre et pas un :
  // chacun porte une SUITE différente — ouvrir l'avoir existant, corriger un
  // montant, ou rien du tout quand le geste n'a pas de sens.
  avoir_sur_avoir:
    'On n’établit pas un avoir sur un avoir. Si cet avoir est faux, c’est une facture qu’il faut établir.',
  facture_deja_avoiree:
    'Cette facture est déjà intégralement avoirée : un second avoir rendrait au client plus qu’il n’a payé. Ouvrez l’avoir existant.',
  avoir_trop_grand:
    'Cet avoir dépasse ce qui reste à avoirer sur cette facture. Le détail affiché sous ce message donne le montant maximal.',
  avoir_impossible:
    'Cet avoir ne peut pas être établi. Le détail affiché sous ce message dit ce qui s’y oppose.',
  // AJOUTÉ LE 10/09/2026. Distinct de `injoignable`, qui parlait de connexion
  // alors que le service avait bel et bien répondu.
  service:
    'Le service a répondu une erreur inattendue. Ce n’est pas votre connexion. Ouvrez la console (F12) : le code y est écrit.',
};

// ---------------------------------------------------------------------------
// La vue — elle dessine, elle ne décide rien
// ---------------------------------------------------------------------------

export interface ProprietesVueImport {
  readonly sorte: SorteImport;
  /** Nul tant qu'aucun fichier n'a été déposé. */
  readonly rapport: RapportImport | null;
  readonly nomFichier: string | null;
  readonly travail: 'repos' | 'modele' | 'analyse' | 'import';
  /** Ce qui vient de réussir. */
  readonly fait: string | null;
  readonly refus: string | null;
  readonly quandSorte: (cle: SorteImport) => void;
  readonly quandModele: () => void;
  readonly quandFichier: (fichier: File) => void;
  readonly quandImporter: () => void;
}

export function VueImport({
  sorte,
  rapport,
  nomFichier,
  travail,
  fait,
  refus,
  quandSorte,
  quandModele,
  quandFichier,
  quandImporter,
}: ProprietesVueImport) {
  const dit = sorteDite(sorte);
  const occupe = travail !== 'repos';
  const pret = rapport !== null && peutImporter(rapport);
  const apercu = rapport === null ? null : apercuDit(rapport);

  return (
    <div className="import">
      <div className="onglets-import">
        {SORTES_IMPORT.map((s) => (
          <button
            key={s.cle}
            type="button"
            className={s.cle === sorte ? 'onglet-import actif' : 'onglet-import'}
            disabled={occupe}
            onClick={() => quandSorte(s.cle)}
          >
            {s.nom}
          </button>
        ))}
      </div>

      <p className="aide-liste">
        {dit.aide}{' '}
        {dit.doublon === null
          ? 'Cette liste n’a aucune règle d’unicité : deux fiches identiques sont acceptées.'
          : `Une ligne portant ${dit.doublon} est refusée.`}
      </p>

      {refus === null ? null : <p className="bandeau bandeau-refus">{refus}</p>}
      {fait === null ? null : <p className="bandeau bandeau-fait">{fait}</p>}

      <ol className="etapes-import">
        <li>
          <span className="quoi">
            <span className="numero-geste">1.</span> Télécharger le modèle
          </span>
          <button
            className="bouton bouton-discret"
            type="button"
            disabled={occupe}
            onClick={quandModele}
          >
            {travail === 'modele' ? 'Préparation…' : nomModele(sorte)}
          </button>
          {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
        </li>

        <li>
          <span className="quoi">
            <span className="numero-geste">2.</span> Déposer le fichier rempli
          </span>
          {/* Le dépôt déclenche l'analyse : il n'y a rien d'autre à faire d'un
              fichier qu'on vient de recevoir, et un second clic serait celui
              qu'on oublie. */}
          <label
            className={
              occupe
                ? 'bouton bouton-discret depot eteint'
                : 'bouton bouton-discret depot'
            }
          >
            {travail === 'analyse' ? 'Lecture…' : 'Choisir un fichier…'}
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={occupe}
              onChange={(evenement) => {
                const fichier = evenement.target.files?.[0];
                if (fichier !== undefined) quandFichier(fichier);
                // LE CHAMP EST REMIS À ZÉRO AUSSITÔT. Sans cela, redéposer LE
                // MÊME fichier après l'avoir corrigé dans Excel ne déclenche
                // rien : le navigateur compare le chemin choisi au précédent et
                // ne signale aucun changement. C'est exactement le geste qu'on
                // fait le plus souvent ici.
                evenement.target.value = '';
              }}
            />
          </label>
          <span className="pourquoi">
            {nomFichier === null
              ? 'Rien n’est écrit à ce moment-là : Stafa lit et vous dit ce qui passerait.'
              : `Lu : ${nomFichier}`}
          </span>
        </li>

        <li>
          <span className="quoi">
            <span className="numero-geste">3.</span> Importer
          </span>
          <button
            className="bouton"
            type="button"
            disabled={occupe || !pret}
            onClick={quandImporter}
          >
            {travail === 'import' ? 'Import…' : 'Importer'}
          </button>
          <span className="pourquoi">
            {rapport === null
              ? 'Déposez d’abord un fichier.'
              : `${resumeAnalyse(rapport)}${apercu === null ? '' : ` — ${apercu}`}`}
          </span>
        </li>
      </ol>

      {rapport !== null && rapport.refus.length > 0 && (
        <ul className="refus-import">
          {rapport.refus.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}

      {rapport !== null && rapport.fautes.length > 0 && (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th className="col-ligne">Ligne</th>
                <th>Ce qui bloque</th>
              </tr>
            </thead>
            <tbody>
              {rapport.fautes.map((faute) => (
                <tr key={faute.numero}>
                  {/* LE NUMÉRO DU TABLEUR, pas un rang de tableau : c'est celui
                      que l'utilisateur voit dans sa marge. */}
                  <td className="code">{faute.numero}</td>
                  <td>{faute.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// L'écran — il décide
// ---------------------------------------------------------------------------

export interface ProprietesImport {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

export function EcranImport({ jeton, quandAccesPerdu }: ProprietesImport) {
  const [sorte, setSorte] = useState<SorteImport>('clients');
  const [rapport, setRapport] = useState<RapportImport | null>(null);
  const [nomFichier, setNomFichier] = useState<string | null>(null);
  const [travail, setTravail] = useState<'repos' | 'modele' | 'analyse' | 'import'>(
    'repos',
  );
  const [fait, setFait] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  /**
   * LE TEXTE DU DERNIER FICHIER LU, gardé hors de React.
   *
   * L'import le renvoie tel quel : c'est le MÊME fichier que celui qui vient
   * d'être analysé, et redemander à l'utilisateur de le redéposer entre
   * l'analyse et l'import laisserait la porte ouverte à importer autre chose
   * que ce qu'il a lu. Dans un état de React, il ferait redessiner l'écran à
   * chaque frappe pour rien.
   */
  const csv = useRef<string>('');

  /** Un accès perdu referme la séance ; tout autre refus s'affiche. */
  const traiter = useCallback(
    (cause: EchecApi) => {
      if (cause === 'acces') {
        quandAccesPerdu();
        return;
      }
      setRefus(REFUS[cause]);
    },
    [quandAccesPerdu],
  );

  const changerDeSorte = useCallback((cle: SorteImport) => {
    // TOUT REPART À ZÉRO. Garder le rapport du fichier précédent laisserait
    // croire qu'on peut importer des clients dans l'onglet des sites.
    setSorte(cle);
    setRapport(null);
    setNomFichier(null);
    setFait(null);
    setRefus(null);
    csv.current = '';
  }, []);

  const prendreLeModele = useCallback(() => {
    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      setTravail('modele');
      setRefus(null);
      const resultat = await telechargerModele(porteur, sorte);
      setTravail('repos');
      if (resultat.etat !== 'ok') {
        traiter(resultat.cause);
        return;
      }
      // Le navigateur enregistre le fichier. L'adresse temporaire est libérée
      // aussitôt : elle retient le contenu en mémoire tant qu'elle vit.
      const adresse = URL.createObjectURL(resultat.valeur);
      const lien = document.createElement('a');
      lien.href = adresse;
      lien.download = nomModele(sorte);
      lien.click();
      URL.revokeObjectURL(adresse);
    })();
  }, [jeton, quandAccesPerdu, sorte, traiter]);

  const lireLeFichier = useCallback(
    (fichier: File) => {
      void (async () => {
        const porteur = await jeton();
        if (porteur === null) {
          quandAccesPerdu();
          return;
        }
        setTravail('analyse');
        setRefus(null);
        setFait(null);
        const texte = await fichier.text();
        csv.current = texte;
        const resultat = await analyserImport(porteur, sorte, texte);
        setTravail('repos');
        setNomFichier(fichier.name);
        if (resultat.etat !== 'ok') {
          setRapport(null);
          traiter(resultat.cause);
          return;
        }
        setRapport(resultat.valeur);
      })();
    },
    [jeton, quandAccesPerdu, sorte, traiter],
  );

  const importer = useCallback(() => {
    void (async () => {
      const porteur = await jeton();
      if (porteur === null) {
        quandAccesPerdu();
        return;
      }
      setTravail('import');
      setRefus(null);
      const resultat = await lancerImport(porteur, sorte, csv.current);
      setTravail('repos');

      if (resultat.etat === 'ok') {
        setFait(importDit(sorteDite(sorte), resultat.importees));
        // LE FICHIER EST OUBLIÉ APRÈS COUP : le redéposer serait la seule
        // façon de tout importer deux fois, et l'écran ne doit pas l'offrir
        // d'un clic.
        setRapport(null);
        setNomFichier(null);
        csv.current = '';
        return;
      }
      if (resultat.etat === 'refuse') {
        // L'API a revu le fichier au moment d'écrire et l'a refusé — une fiche
        // a pu être créée entre-temps par quelqu'un d'autre. On remplace le
        // rapport par le sien : c'est lui qui est à jour.
        setRapport(resultat.rapport);
        setRefus('Le fichier n’a pas été importé. Corrigez les lignes ci-dessous.');
        return;
      }
      traiter(resultat.cause);
    })();
  }, [jeton, quandAccesPerdu, sorte, traiter]);

  return (
    <VueImport
      sorte={sorte}
      rapport={rapport}
      nomFichier={nomFichier}
      travail={travail}
      fait={fait}
      refus={refus}
      quandSorte={changerDeSorte}
      quandModele={prendreLeModele}
      quandFichier={lireLeFichier}
      quandImporter={importer}
    />
  );
}
